import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../utils/api';

// Query keys
const CLIENTS_QUERY_KEY = 'clients';

// Custom hook for fetching clients
export const useClients = (page = 1, limit = 10) => {
  return useQuery({
    queryKey: [CLIENTS_QUERY_KEY, page, limit],
    queryFn: async () => {
      const response = await apiClient.get('our-clients', {
        page,
        limit
      });
      return {
        clients: response.clients || response.data || [],
        total: response.total || response.length || 0
      };
    },
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Custom hook for creating a client
export const useCreateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (clientData) => {
      // Check if clientData is FormData (for file uploads)
      if (clientData instanceof FormData) {
        return apiClient.postFormData('our-clients', clientData);
      }
      return apiClient.post('our-clients', clientData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_QUERY_KEY] });
    },
  });
};

// Custom hook for updating a client
export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, clientData }) => {
      // Check if clientData is FormData (for file uploads)
      if (clientData instanceof FormData) {
        // For Laravel applications, use POST with _method: PUT when sending FormData
        return apiClient.postFormData(`our-clients/${id}`, clientData);
      }
      return apiClient.put(`our-clients/${id}`, clientData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_QUERY_KEY] });
    },
  });
};

// Custom hook for deleting a client
export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => apiClient.delete(`our-clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_QUERY_KEY] });
    },
  });
};

// Custom hook for getting a single client
export const useClient = (id) => {
  return useQuery({
    queryKey: [CLIENTS_QUERY_KEY, id],
    queryFn: () => apiClient.get(`our-clients/${id}`),
    enabled: !!id,
  });
}; 